import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import axios from 'axios';

export const getSalesSiteApiBase = () => {
  const configured = process.env.REACT_APP_API_URL;
  const base = configured || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api/' : '/api/');
  return base.endsWith('/') ? base : `${base}/`;
};

export const buildPreviewDocument = (html, apiBase) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.documentElement.dataset.editorPreview = 'true';
  doc.documentElement.dataset.editorOrigin = window.location.origin;
  doc.querySelectorAll('base, meta[http-equiv="Content-Security-Policy"]').forEach(node => node.remove());
  const base = doc.createElement('base');
  base.href = new URL(apiBase, window.location.href).origin + '/';
  doc.head.prepend(base);
  const policy = doc.createElement('meta');
  policy.httpEquiv = 'Content-Security-Policy';
  policy.content = "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline' https:; img-src http: https: data:; font-src https: data:; connect-src 'none'; form-action 'none'; base-uri http: https:";
  doc.head.prepend(policy);
  return `<!DOCTYPE html>${doc.documentElement.outerHTML}`;
};

const SitePreview = ({ settings, previewKey = 0, style }) => {
  const iframeRef = useRef(null);
  const [html, setHtml] = useState('');
  const [items, setItems] = useState([]);
  const [catalogError, setCatalogError] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setHtml('');
    setItems([]);
    setCatalogError(false);
    const apiBase = getSalesSiteApiBase();
    const options = {
      headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      signal: controller.signal
    };
    Promise.all([
      axios.get(`${apiBase}sales-site/settings/proxy`, { ...options, responseType: 'text' }),
      axios.get(`${apiBase}sales-site/settings/public/catalog-filters`, options).catch(() => null)
    ]).then(([response, catalog]) => {
      if (typeof response.data !== 'string' || !response.data.includes('<html')) {
        throw new Error('Invalid preview template');
      }
      if (active) {
        const available = catalog?.data?.success === true && Array.isArray(catalog.data.data?.items);
        setItems(available ? catalog.data.data.items.slice(0, 100) : []);
        setCatalogError(!available);
        setHtml(buildPreviewDocument(response.data, apiBase));
      }
    }).catch(() => {
      if (active) setError('Не удалось загрузить предпросмотр. Проверьте доступность сервиса сайта.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [previewKey]);

  const sendSettings = useCallback(() => {
    if (settings && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: 'UPDATE_SETTINGS', settings, items }, '*');
    }
  }, [settings, items]);

  useEffect(() => {
    const timer = setTimeout(sendSettings, 100);
    return () => clearTimeout(timer);
  }, [sendSettings, html]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (loading) return <Box sx={{ p: 3 }}><CircularProgress size={24} aria-label="Загрузка предпросмотра" /></Box>;
  return (
    <>
      {catalogError && <Alert severity="warning">Данные каталога недоступны в предпросмотре.</Alert>}
      <iframe
        ref={iframeRef}
        srcDoc={html}
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        title="Предпросмотр сайта"
        onLoad={sendSettings}
        style={{ width: '100%', height: '100%', border: 'none', ...style }}
      />
    </>
  );
};

export default SitePreview;
